import { useEffect, useMemo, useRef } from 'react';
import { Gift } from 'lucide-react';
import { useCurrencyStore } from './stores/useCurrencyStore';
import { useUIStore } from './stores/useUIStore';
import { useShopStore } from './stores/useShopStore';
import { useCartStore } from './stores/useCartStore';
import { useAuthStore } from './stores/useAuthStore';
import { ShopItem, DetailModalItem } from './types';
import { RawShopEntry, parseRawEntryItem } from './utils/fortniteShopParser';
import {
  Header,
  DailyCountdown,
} from './components/layout';
import { CatalogGrid, ItemDetailModal, FloatingShopControls } from './components/shop';
import { CartDrawer } from './components/cart';
import { OrderPage } from './components/checkout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { lockScroll, unlockScroll } from './utils/scrollLock';
import { startShopBackgroundPrefetch } from './services/shopPrefetcher';

// New Pages & Modals
import { LandingPage } from './components/landing/LandingPage';
import { CartPage } from './components/cart/CartPage';
import { ProfilePage } from './components/profile/ProfilePage';
import { RecargasPage } from './components/recargas/RecargasPage';
import { SorteosPage } from './components/sorteos/SorteosPage';
import { DepositModal } from './components/deposit/DepositModal';
import { RaffleModal } from './components/raffle/RaffleModal';
import { AuthModal } from './components/auth/AuthModal';
import { LinkAccountModal } from './components/profile/LinkAccountModal';

export default function App() {
  // Navigation & Active Page
  const activePage = useUIStore((s) => s.activePage);
  const activeOrderCode = useUIStore((s) => s.activeOrderCode);
  const openDeposit = useUIStore((s) => s.openDepositModal);
  const openRaffle = useUIStore((s) => s.openRaffleModal);
  const openAuth = useUIStore((s) => s.openAuthModal);
  const openLinkAccount = useUIStore((s) => s.openLinkAccountModal);

  // Scroll tracking and position preservation across SPA navigation
  const isRestoringScrollRef = useRef(false);
  const prevPageRef = useRef<string>(activePage);

  // Continuously record scroll position of the currently active page in UIStore
  useEffect(() => {
    const handleScroll = () => {
      if (!isRestoringScrollRef.current) {
        useUIStore.getState().saveScrollPosition(activePage, window.scrollY);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activePage]);

  // Restore scroll position when navigating back or between pages
  useEffect(() => {
    prevPageRef.current = activePage;
    const targetY = useUIStore.getState().scrollPositions[activePage] || 0;
    isRestoringScrollRef.current = true;

    window.scrollTo({ top: targetY, left: 0, behavior: 'instant' as ScrollBehavior });
    requestAnimationFrame(() => {
      window.scrollTo({ top: targetY, left: 0, behavior: 'instant' as ScrollBehavior });
      requestAnimationFrame(() => {
        window.scrollTo({ top: targetY, left: 0, behavior: 'instant' as ScrollBehavior });
        setTimeout(() => {
          isRestoringScrollRef.current = false;
        }, 100);
      });
    });
  }, [activePage]);

  // Currency Store
  const currency = useCurrencyStore((s) => s.currency);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);

  // Shop Store
  const isLoading = useShopStore((s) => s.isLoading);
  const fetchShop = useShopStore((s) => s.fetchShop);
  const getOrderedSections = useShopStore((s) => s.getOrderedSections);
  const resetFilters = useShopStore((s) => s.resetFilters);

  // UI Store
  const searchQuery = useUIStore((s) => s.searchQuery);
  const setSearchQuery = useUIStore((s) => s.setSearchQuery);

  const isCartOpen = useUIStore((s) => s.isCartOpen);
  const openCart = useUIStore((s) => s.openCart);
  const closeCart = useUIStore((s) => s.closeCart);

  const selectedItem = useUIStore((s) => s.selectedItem);
  const isDetailModalOpen = useUIStore((s) => s.isDetailModalOpen);
  const openItemDetail = useUIStore((s) => s.openItemDetail);
  const closeItemDetail = useUIStore((s) => s.closeItemDetail);

  // Ensure clean scroll reset on mount / reload without jumping & revalidate user profile from DB
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      window.scrollTo(0, 0);
    }
    useAuthStore.getState().fetchMe();
  }, []);

  // Listen for browser back / forward navigation
  useEffect(() => {
    const handlePopState = () => {
      // Save current scroll position before page state switches
      const currentY = window.scrollY;
      const currentPage = useUIStore.getState().activePage;
      useUIStore.getState().saveScrollPosition(currentPage, currentY);

      const path = window.location.pathname.toLowerCase();
      if (path.includes('/tienda') || path.includes('tienda.html')) {
        useUIStore.setState({ activePage: 'shop' });
      } else if (
        path.includes('/cart') ||
        path.includes('cart.html') ||
        path.includes('/carrito')
      ) {
        useUIStore.setState({ activePage: 'cart' });
      } else if (
        path.includes('/profile') ||
        path.includes('profile.html') ||
        path.includes('/perfil')
      ) {
        useUIStore.setState({ activePage: 'profile' });
      } else if (path.includes('/recargas') || path.includes('recargas.html')) {
        useUIStore.setState({ activePage: 'recargas' });
      } else if (path.includes('/sorteos') || path.includes('sorteos.html')) {
        useUIStore.setState({ activePage: 'sorteos' });
      } else if (path.includes('/order') || path.includes('order.html') || path.includes('/pedido')) {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        useUIStore.setState({ activePage: 'order', ...(code ? { activeOrderCode: code } : {}) });
      } else {
        useUIStore.setState({ activePage: 'landing' });
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Register global window helper functions for modal triggers
  useEffect(() => {
    (window as any).openDepositModal = (amt?: number) => openDeposit(amt);
    (window as any).triggerDepositModal = (amt?: number) => openDeposit(amt);
    (window as any).openRafflePopup = () => openRaffle();
    (window as any).triggerRafflePopup = () => openRaffle();
    (window as any).openAuthModal = (tab?: 'login' | 'register') => openAuth(tab);
    (window as any).triggerAuthModal = (tab?: 'login' | 'register') => openAuth(tab);
    (window as any).openLinkFortniteModal = () => openLinkAccount();
  }, [openDeposit, openRaffle, openAuth, openLinkAccount]);

  // Fetch shop items on mount & currency change
  useEffect(() => {
    fetchShop(currency);
  }, [currency, fetchShop]);

  // Start background download of shop JSON and images while browsing index
  useEffect(() => {
    startShopBackgroundPrefetch();
  }, []);

  // Auto-refrescar catálogo si la ventana recupera el foco después de una rotación
  useEffect(() => {
    let lastFetch = Date.now();
    const handleFocus = () => {
      if (Date.now() - lastFetch > 45000) {
        lastFetch = Date.now();
        fetchShop(currency);
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [currency, fetchShop]);

  // Cart Store for global reactive purchasing
  const cartItems = useCartStore((s) => s.items);
  const addItemToCart = useCartStore((s) => s.addItem);
  const removeItemFromCart = useCartStore((s) => s.removeItem);
  const isInCart = useCartStore((s) => s.isInCart);

  const cartOfferIds = useMemo(
    () => new Set(cartItems.map((ci) => ci.offerId)),
    [cartItems]
  );

  const cartCount = useMemo(
    () => cartItems.reduce((acc, curr) => acc + curr.quantity, 0),
    [cartItems]
  );

  const handleToggleCartEntry = (entry: RawShopEntry) => {
    const parsed = parseRawEntryItem(entry);
    if (!parsed) return;
    const rate = currency === 'MXN' ? 0.092 : 7.499;
    const shopItem: ShopItem = {
      id: entry.offerId,
      name: parsed.name,
      description: parsed.description || undefined,
      vbucks: entry.finalPrice,
      finalPrice: entry.finalPrice,
      price: Math.round(entry.finalPrice * rate),
      currency,
      image: parsed.image,
      images: {
        icon: parsed.image,
        featured: parsed.image,
      },
      rarity: parsed.rarity?.displayValue || 'Raro',
      category: parsed.isBundle ? 'LOTES' : parsed.type?.displayValue || 'OBJETO',
      section: entry.layout?.name || entry.layout?.category || 'Destacados',
      isBundle: parsed.isBundle,
    };

    if (isInCart(shopItem.id)) {
      removeItemFromCart(shopItem.id);
    } else {
      addItemToCart(shopItem, 1);
    }
  };

  const handleOpenDetailEntry = (entry: RawShopEntry) => {
    const parsed = parseRawEntryItem(entry);
    if (!parsed) return;
    const rate = currency === 'MXN' ? 0.092 : 7.499;
    const safePrice = entry.finalPrice ?? entry.regularPrice ?? 0;
    const detailItem: DetailModalItem = {
      id: entry.offerId,
      name: parsed.name,
      description: parsed.description || undefined,
      vbucks: safePrice,
      finalPrice: safePrice,
      price: Math.round(safePrice * rate),
      currency,
      image: parsed.image,
      images: {
        icon: parsed.image,
        featured: parsed.image,
      },
      rarity: parsed.rarity?.displayValue || 'Raro',
      category: parsed.isBundle ? 'LOTES' : parsed.type?.displayValue || 'OBJETO',
      section: entry.layout?.name || entry.layout?.category || 'Destacados',
      isBundle: parsed.isBundle,
      rawEntry: entry,
      outDate: entry.outDate,
      set: entry.brItems?.[0]?.set?.text || entry.brItems?.[0]?.set?.value,
      introduction: entry.brItems?.[0]?.introduction?.text,
      series: parsed.series?.value,
      colors: {
        a: (entry.colors as any)?.Color1 || (entry.colors as any)?.color1,
        b: (entry.colors as any)?.Color2 || (entry.colors as any)?.color3,
      },
    };
    openItemDetail(detailItem);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    useShopStore.getState().setSearchQuery(query);
  };

  // Get authentic ordered sections (memoized so modal open/close doesn't thrash array reference)
  const rawEntries = useShopStore((s) => s.rawEntries);
  const activeCategory = useShopStore((s) => s.activeCategory);
  const storeSearchQuery = useShopStore((s) => s.searchQuery);

  const orderedSections = useMemo(
    () => getOrderedSections(),
    [getOrderedSections, rawEntries, activeCategory, storeSearchQuery]
  );
  const totalMatchingItems = useMemo(
    () => orderedSections.reduce((acc, [, items]) => acc + items.length, 0),
    [orderedSections]
  );

  const isDepositModalOpen = useUIStore((s) => s.isDepositModalOpen);
  const isRaffleModalOpen = useUIStore((s) => s.isRaffleModalOpen);
  const isAuthModalOpen = useUIStore((s) => s.isAuthModalOpen);
  const isLinkAccountModalOpen = useUIStore((s) => s.isLinkAccountModalOpen);
  const isCheckoutOpen = useUIStore((s) => s.isCheckoutOpen);
  const isOrderModalOpen = useUIStore((s) => s.isOrderModalOpen);

  const isAnyModalOpen =
    isCartOpen ||
    isDetailModalOpen ||
    isDepositModalOpen ||
    isRaffleModalOpen ||
    isAuthModalOpen ||
    isLinkAccountModalOpen ||
    isCheckoutOpen ||
    isOrderModalOpen;

  useEffect(() => {
    if (isAnyModalOpen) {
      lockScroll();
      return () => {
        unlockScroll();
      };
    }
  }, [isAnyModalOpen]);

  return (
    <div
      className="min-h-screen flex flex-col font-sans selection:bg-[#1447e6]/50 selection:text-white relative w-full overflow-x-clip"
      style={{
        background:
          'linear-gradient(rgba(15, 23, 42, 0.045) 1px, transparent 1px) 0 0/44px 44px, linear-gradient(90deg, rgba(15, 23, 42, 0.045) 1px, transparent 1px) 0 0/44px 44px, #f2f4f7',
        color: '#06173f',
      }}
    >
      {/* 1. Sticky Header Oficial GF (Logo inicio, Saldo, Carrito, Menú Hamburguesa) */}
      <Header
        currency={currency}
        onCurrencyChange={setCurrency}
        cartCount={cartCount}
        onOpenCart={openCart}
      />

      {/* 2. RENDER ACTIVE PAGE (LandingPage state and scroll preserved across navigations) */}
      <div
        id="page-landing-container"
        className={activePage === 'landing' ? 'contents' : 'hidden'}
        style={{ display: activePage === 'landing' ? 'contents' : 'none' }}
      >
        <LandingPage />
      </div>

      {activePage === 'cart' && <CartPage />}

      {activePage === 'profile' && <ProfilePage />}

      {activePage === 'recargas' && <RecargasPage />}

      {activePage === 'sorteos' && <SorteosPage />}

      {activePage === 'order' && (
        <OrderPage
          key={activeOrderCode || 'order-page'}
          orderCode={activeOrderCode || undefined}
        />
      )}

      {activePage === 'shop' && (
        <>
          {/* Contenedor Flotante: Índice y Buscador juntos */}
          <FloatingShopControls
            sections={orderedSections}
            searchQuery={searchQuery}
            onSearchChange={handleSearchChange}
            totalMatchingItems={totalMatchingItems}
          />

          {/* Main Content Area */}
          <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:pl-20 xl:px-6 py-4 md:py-6">
            <CatalogGrid
              sections={orderedSections}
              isLoading={isLoading}
              currency={currency}
              cartOfferIds={cartOfferIds}
              onToggleCart={handleToggleCartEntry}
              onOpenDetail={handleOpenDetailEntry}
              onResetFilters={resetFilters}
            />
          </main>

          {/* Floating Action Elements (Suppressed when modals are open) */}
          {!isAnyModalOpen && (
            <>
              {/* Shop Reset Daily Countdown Widget */}
              <DailyCountdown variant="floating" onReset={() => fetchShop(currency)} />

              {/* Desktop Weekly Raffle Gift Floating Button */}
              <button
                type="button"
                onClick={openRaffle}
                className="hidden md:flex fixed bottom-8 right-8 z-40 w-14 h-14 bg-[#1447e6] hover:bg-[#0a2a9c] clip-cut items-center justify-center text-white shadow-[0_8px_24px_rgba(20,71,230,0.45)] hover:shadow-[0_12px_28px_rgba(6,23,63,0.55)] transition-all hover:-translate-y-1 active:scale-95 group cursor-pointer"
                title="¡Sorteo Semanal de Pavos!"
              >
                <Gift className="w-6 h-6 group-hover:scale-110 transition-transform" />
                <span className="absolute top-2 right-2 w-2.5 h-2.5 bg-[#5ccbff] border-2 border-white rounded-full shadow-[0_0_8px_#5ccbff] animate-pulse" />
              </button>

              {/* Mobile Floating Cart Button (bottom-right) */}
              <div
                className="md:hidden fixed bottom-5 right-4 z-40"
                style={{ filter: 'drop-shadow(4px 4px 0 #1447e6)' }}
              >
                <button
                  type="button"
                  onClick={openCart}
                  className="relative w-14 h-14 bg-white text-[#1447e6] hover:text-[#0a2a9c] flex items-center justify-center transition-transform active:scale-95 cursor-pointer shadow-lg"
                  style={{ clipPath: 'var(--g-cut)' }}
                  title="Ver carrito"
                  aria-label="Ver carrito"
                >
                  <i className="fa-solid fa-cart-shopping text-xl text-[#1447e6]" />
                  {cartCount > 0 && (
                    <span
                      className="absolute top-1 right-1 min-w-[20px] h-5 px-1 bg-[#1447e6] text-white flex items-center justify-center font-chakra font-bold text-xs"
                      style={{ clipPath: 'var(--g-slash)' }}
                    >
                      {cartCount}
                    </span>
                  )}
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* 3. Global Modals */}
      <DepositModal />
      <RaffleModal />
      <AuthModal />
      <LinkAccountModal />

      {/* Item Detail Modal */}
      <ErrorBoundary fallback={null} name="ItemDetailModal">
        {selectedItem && (
          <ItemDetailModal
            isOpen={isDetailModalOpen}
            onClose={closeItemDetail}
            item={selectedItem}
            isInCart={cartOfferIds.has(selectedItem.id)}
            onToggleCart={(it) => {
              if (isInCart(it.id)) {
                removeItemFromCart(it.id);
              } else {
                addItemToCart(it, 1);
              }
            }}
            onAddToCart={(it) => addItemToCart(it, 1)}
          />
        )}
      </ErrorBoundary>

      {/* Reactive Cart Drawer Panel */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={closeCart}
      />

      {/* 4. Footer Oficial */}
      <footer className="border-t border-[#cfd7e3] bg-[#06173f] text-white py-8 px-4 text-center text-xs space-y-2 mt-auto">
        <p className="font-heading uppercase tracking-wider text-slate-200">
          elGordoFortnite • Tienda Automatizada 24/7 • 2026
        </p>
        <p className="text-[11px] text-slate-400 max-w-md mx-auto">
          Esta web no está afiliada, respaldada ni patrocinada por Epic Games, Inc.
        </p>
      </footer>
    </div>
  );
}
